import React, { useState, useEffect, useMemo } from 'react';
import { Button, Col, Input, Label, Row, Spinner, Badge } from 'reactstrap';
import { PreviewCard, RSelect } from '../../../components/Component';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { DealCategory, formatValue } from '../../../utils/envConfig';
import { toast } from 'react-toastify';
import SearchClientSelect from '../../../components/SearchClientSelect';
import { timeAsMilliseconds } from '../../../utils/Utils';
import RSelectAsync from '../../../components/select/ReactSelectAsync';

const EOIForm = ({ dealType, next }) => {
    const axios = useAxiosPrivate();
    const queryClient = useQueryClient();

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [filteredOffplans, setFilteredOffplans] = useState([]);
    const [availablePropertyTypes, setAvailablePropertyTypes] = useState([]);
    const [availableBeds, setAvailableBeds] = useState([]);
    const [priceInfo, setPriceInfo] = useState(null);
    const [dealInfo, setDealInfo] = useState({
        client: null,
        agent: null,
        developer: null,
        project: null,
        propertyType: null,
        beds: null,
        estimatedPrice: '',
        paidAmount: '',
        notes: '',
        agreedCommission: '',
        agreedCommissionValue: ''
    });
    const [formattedCommission, setFormattedCommission] = useState('');

    // Fetch offplans using React Query
    const { data: offplans, isf } = useQuery(
        ['offplans', dealInfo.developer],
        async () => {
            const baseUrl = '/off-plan?per_page=100';
            const developerQuery = dealInfo.developer ? `&developer=${dealInfo.developer.value}` : '';
            const { data } = await axios.get(`${baseUrl}${developerQuery}`);
            return data;
        },
        {
            staleTime: timeAsMilliseconds({ hours: 1 }),
            refetchOnWindowFocus: false
        }
    );

    // Fetch developers using React Query
    const { data: developers } = useQuery(
        ['developers'],
        async () => {
            const { data } = await axios.get('/off-plan/plan-developer');
            return data;
        },
        {
            staleTime: timeAsMilliseconds({ hours: 1 }),
            refetchOnWindowFocus: false
        }
    );

    // Fetch agents using React Query
    const { data: agents } = useQuery(
        ['agents'],
        async () => {
            const { data } = await axios.get('/agent');
            return data;
        },
        {
            staleTime: timeAsMilliseconds({ hours: 1 }),
            refetchOnWindowFocus: false
        }
    );

    // Initialize all offplans with useMemo to prevent unnecessary recalculations
    const allOffplansOptions = useMemo(() => {
        return offplans?.filteredOutput?.length
            ? offplans.filteredOutput.map((item) => ({
                ...item,
                value: item?.id || item?._id,
                label: item?.developmentName
            }))
            : [];
    }, [offplans]);

    // Use filtered offplans if available, otherwise use all offplans
    const offplansOptions = filteredOffplans.length > 0
        ? filteredOffplans
        : allOffplansOptions;

    const developerOptions = developers?.length
        ? developers.map((item) => ({
            ...item,
            value: item?.id || item?._id,
            label: item?.name
        }))
        : [];

    const agentOptions = agents?.length
        ? agents.map((agent) => ({
            ...agent,
            value: agent?.id || agent?._id,
            label: `${agent?.user?.first_name} ${agent?.user?.last_name}`
        }))
        : [];

    // Use available property types if a project is selected, otherwise use default options
    const propertyTypeOptions = availablePropertyTypes.length > 0
        ? availablePropertyTypes
        : [
            { value: 'Apartment', label: 'Apartment' },
            { value: 'Villa', label: 'Villa' },
            { value: 'Townhouse', label: 'Townhouse' }
        ];



    // Initialize filtered offplans when component mounts or when offplans data changes
    useEffect(() => {
        if (offplans?.filteredOutput?.length) {
            setFilteredOffplans(allOffplansOptions);
        }
    }, [offplans, allOffplansOptions]);

    // Handle developer selection
    const handleDeveloperChange = (developer) => {
        // Reset project, property type, and beds if developer changes
        setDealInfo({
            ...dealInfo,
            developer,
            project: null,
            propertyType: null,
            beds: null
        });

        // Filter offplans by selected developer
        if (developer) {
            const filteredProjects = allOffplansOptions.filter(
                project => project.developer?._id === developer.value || project.developer === developer.value
            );
            setFilteredOffplans(filteredProjects);
        } else {
            // If no developer selected, show all offplans
            setFilteredOffplans(allOffplansOptions);
        }

        // Reset available property types and beds
        setAvailablePropertyTypes([]);
        setAvailableBeds([]);
        setPriceInfo(null);
    };

    // Handle project selection
    const handleProjectChange = (project) => {
        if (!project) {
            // If project is cleared, reset property type and beds
            setDealInfo({
                ...dealInfo,
                project: null,
                propertyType: null,
                beds: null
            });
            setAvailablePropertyTypes([]);
            setAvailableBeds([]);
            setPriceInfo(null);
            return;
        }

        // Find the developer for this project
        const projectDeveloper = project.developer;
        let developerObj = null;

        // Find the developer in the options
        if (projectDeveloper) {
            developerObj = developerOptions.find(
                dev => dev.value === (projectDeveloper?._id || projectDeveloper)
            );
        }

        // Update deal info with project and its developer
        setDealInfo({
            ...dealInfo,
            project,
            developer: developerObj,
            propertyType: null,
            beds: null
        });

        // Extract available property types from the project
        if (project.propertyTypes?.length) {
            const propertyTypes = project.propertyTypes.map(pt => ({
                value: pt.propertyType,
                label: pt.propertyType,
                beds: pt.beds
            }));
            setAvailablePropertyTypes(propertyTypes);
        } else {
            setAvailablePropertyTypes([]);
        }

        setAvailableBeds([]);
        setPriceInfo(null);
    };

    // Handle property type selection
    const handlePropertyTypeChange = (propertyType) => {
        setDealInfo({
            ...dealInfo,
            propertyType,
            beds: null
        });

        // Find the selected project
        const selectedProject = dealInfo.project;

        if (selectedProject && propertyType) {
            // Find the property type in the project
            const selectedPropertyTypeData = selectedProject.propertyTypes?.find(
                pt => pt.propertyType === propertyType.value
            );

            // Extract available beds for this property type
            if (selectedPropertyTypeData?.beds?.length) {
                const bedsOptions = selectedPropertyTypeData.beds.map(bed => ({
                    value: bed.name,
                    label: bed.name,
                    size: bed.size,
                    startingPrice: bed.startingPrice,
                    permitNumber: bed.permitNumber
                }));
                setAvailableBeds(bedsOptions);
            } else {
                setAvailableBeds([]);
            }
        } else {
            setAvailableBeds([]);
        }

        setPriceInfo(null);
    };

    // Handle beds selection
    const handleBedsChange = (beds) => {
        setDealInfo({
            ...dealInfo,
            beds
        });

        // Set price info if available
        if (beds) {
            setPriceInfo({
                size: beds.size,
                startingPrice: beds.startingPrice,
                permitNumber: beds.permitNumber
            });

            // Auto-fill estimated price if available
            if (beds.startingPrice) {
                setDealInfo(prev => ({
                    ...prev,
                    beds,
                    estimatedPrice: beds.startingPrice.toString()
                }));
            }
        } else {
            setPriceInfo(null);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setDealInfo({
            ...dealInfo,
            [name]: value
        });
    };

    // Sync commission fields when estimatedPrice changes
    useEffect(() => {
        const price = +dealInfo.estimatedPrice.toString().replaceAll(',', '');
        const percent = dealInfo.agreedCommission;
        if (price > 0 && percent) {
            setDealInfo((prev) => ({
                ...prev,
                agreedCommissionValue: ((price * percent) / 100).toLocaleString()
            }));
        }
    }, [dealInfo.estimatedPrice]);

    const validateForm = () => {
        const newErrors = {};

        if (!dealInfo.client) newErrors.client = 'Client is required';
        if (!dealInfo.agent) newErrors.agent = 'Agent is required';
        if (!dealInfo.developer) newErrors.developer = 'Developer is required';
        if (!dealInfo.project) newErrors.project = 'Project is required';
        if (!dealInfo.propertyType) newErrors.propertyType = 'Property type is required';
        if (!dealInfo.beds) newErrors.beds = 'Beds is required';
        if (!dealInfo.estimatedPrice) newErrors.estimatedPrice = 'Estimated price is required';
        if (!dealInfo.paidAmount) newErrors.paidAmount = 'Paid amount is required';
        if (!dealInfo.agreedCommission) newErrors.agreedCommission = 'Agreed commission (%) is required';
        if (!dealInfo.agreedCommissionValue) newErrors.agreedCommissionValue = 'Agreed commission (AED) is required';
        else {
            const price = +dealInfo.estimatedPrice.toString().replaceAll(',', '');
            const percent = +dealInfo.agreedCommission;
            const value = +dealInfo.agreedCommissionValue.toString().replaceAll(',', '');
            if (percent > 100) newErrors.agreedCommission = 'Cannot be more than 100%';
            if (value > price) newErrors.agreedCommissionValue = 'Cannot be more than estimated price';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Create deal mutation
    const createDealMutation = useMutation(
        (dealData) => axios.post('/deals', dealData),
        {
            onSuccess: (response) => {
                localStorage.setItem('new_deal', JSON.stringify(response.data));
                toast.success('Expression of Interest created successfully');
                queryClient.invalidateQueries('deals');
                next();
            },
            onError: (error) => {
                toast.error(error?.response?.data?.message || 'Failed to create EOI');
                console.log(error);
            },
            onSettled: () => {
                setLoading(false);
            }
        }
    );

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);

        const dealData = {
            category: DealCategory.EOI,
            type: 'Offplan',
            user_id: dealInfo.client?.id || dealInfo.client?._id,
            assignedAgent: dealInfo.agent?.id || dealInfo.agent?._id,
            developer_id: dealInfo.developer?.id || dealInfo.developer?._id,
            offplan_id: dealInfo.project?.id || dealInfo.project?._id,
            unitDetails: {
                propertyType: dealInfo.propertyType?.value,
                beds: dealInfo.beds?.value
            },
            estimated_price: +dealInfo.estimatedPrice.replaceAll(',', ''),
            paid_amount: +dealInfo.paidAmount.replaceAll(',', ''),
            notes: dealInfo.notes,
            agreedCommission: +dealInfo.agreedCommission,
            completionDate: dealInfo.project?.completionDate
        };

        createDealMutation.mutate(dealData);
    };

    console.log(dealInfo, "deal info")

    return (
        <PreviewCard>

            <Row className="gy-4">
                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Client *</Label>
                        <div className="form-control-wrap">
                            <SearchClientSelect
                                UsersOrOwners={true}
                                onChange={(client) => {
                                    // Check if client has currentAgent
                                    let agentValue = null;
                                    if (client?.currentAgent && client?.currentAgent?.id) {
                                        // Format the agent data to match the expected format for RSelect
                                        agentValue = {
                                            ...client.currentAgent,
                                            value: client.currentAgent.id || client.currentAgent._id,
                                            label: client.currentAgent.userId ?
                                                `${client.currentAgent.userId.first_name || ''} ${client.currentAgent.userId.last_name || ''}` :
                                                `Agent ${client.currentAgent.id || client.currentAgent._id}`
                                        };
                                    }

                                    // Update dealInfo with client and agent if available
                                    setDealInfo({
                                        ...dealInfo,
                                        client,
                                        agent: agentValue || dealInfo.agent
                                    });
                                }}
                                value={dealInfo.client}
                            />
                            {errors.client && <span className="text-danger">{errors.client}</span>}
                        </div>
                    </div>
                </Col>

                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Agent *</Label>
                        <div className="form-control-wrap">
                            <RSelect
                                options={agentOptions}
                                onChange={(agent) => setDealInfo({ ...dealInfo, agent })}
                                value={dealInfo.agent}
                            />
                            {errors.agent && <span className="text-danger">{errors.agent}</span>}
                        </div>
                    </div>
                </Col>

                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Developer *</Label>
                        <div className="form-control-wrap">
                            <RSelect
                                options={developerOptions}
                                onChange={handleDeveloperChange}
                                value={dealInfo.developer}
                            />
                            {errors.developer && <span className="text-danger">{errors.developer}</span>}
                        </div>
                    </div>
                </Col>

                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Project *</Label>
                        <div className="form-control-wrap">
                            <RSelect
                                key={dealInfo.developer?.value}
                                options={offplansOptions}
                                onChange={handleProjectChange}
                                value={dealInfo.project}
                                isDisabled={!dealInfo.developer}
                            />
                            {errors.project && <span className="text-danger">{errors.project}</span>}
                            {dealInfo.project?.completionDate && <div className="mt-2">
                                <Badge color="azure" className="py-1 rounded">Completion Date: {dealInfo.project?.completionDate}</Badge>
                            </div>}
                        </div>
                    </div>
                </Col>

                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Property Type *</Label>
                        <div className="form-control-wrap">
                            <RSelect
                                options={propertyTypeOptions}
                                onChange={handlePropertyTypeChange}
                                value={dealInfo.propertyType}
                                isDisabled={!dealInfo.project}
                            />
                            {errors.propertyType && <span className="text-danger">{errors.propertyType}</span>}
                        </div>
                    </div>
                </Col>

                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Beds *</Label>
                        <div className="form-control-wrap">
                            <RSelect
                                options={availableBeds.length > 0
                                    ? availableBeds
                                    : [1, 2, 3, 4, 5, 6].map(num => ({ value: num, label: num }))}
                                onChange={handleBedsChange}
                                value={dealInfo.beds}
                                isDisabled={!dealInfo.propertyType}
                            />
                            {errors.beds && <span className="text-danger">{errors.beds}</span>}
                            {priceInfo && (
                                <div className="mt-2">
                                    <Badge color="primary" className="mr-1 py-1">Size: {!!priceInfo.size ? `${priceInfo.size} sqft` : 'N/A'}</Badge>
                                    <Badge className='ms-1 py-1' color="secondary">Starting Price: AED {formatValue(priceInfo.startingPrice)}</Badge>
                                    {priceInfo.permitNumber && (
                                        <Badge color="info" className="ml-1 py-1">Permit: {priceInfo.permitNumber}</Badge>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </Col>

                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Estimated Price (AED) *</Label>
                        <div className="form-control-wrap">
                            <Input
                                type="text"
                                name="estimatedPrice"
                                value={formatValue(dealInfo.estimatedPrice)}
                                onChange={(e) => setDealInfo({
                                    ...dealInfo,
                                    estimatedPrice: e.target.value.replace(/[^0-9,]/g, '')
                                })}
                            />
                            {errors.estimatedPrice && <span className="text-danger">{errors.estimatedPrice}</span>}
                        </div>
                    </div>
                </Col>

                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Paid Amount (AED) *</Label>
                        <div className="form-control-wrap">
                            <Input
                                type="text"
                                name="paidAmount"
                                value={formatValue(dealInfo.paidAmount)}
                                onChange={(e) => setDealInfo({
                                    ...dealInfo,
                                    paidAmount: e.target.value.replace(/[^0-9,]/g, '')
                                })}
                            />
                            {errors.paidAmount && <span className="text-danger">{errors.paidAmount}</span>}
                        </div>
                    </div>
                </Col>

                {/* Agreed Commission Fields */}
                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Agreed Commission (%) *</Label>
                        <div className="form-control-wrap">
                            <Input
                                type="number"
                                name="agreedCommission"
                                min="0"
                                max="100"
                                value={formattedCommission || dealInfo.agreedCommission}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDealInfo({
                                        ...dealInfo,
                                        agreedCommission: value
                                    });
                                    setFormattedCommission(value);
                                    // Update value field
                                    const price = +dealInfo.estimatedPrice.toString().replaceAll(',', '');
                                    if (!isNaN(price) && price > 0 && value) {
                                        setDealInfo((prev) => ({
                                            ...prev,
                                            agreedCommissionValue: ((price * value) / 100).toLocaleString()
                                        }));
                                    }
                                }}
                            />
                            {errors.agreedCommission && <span className="text-danger">{errors.agreedCommission}</span>}
                        </div>
                    </div>
                </Col>
                <Col md="6">
                    <div className="form-group">
                        <Label className="form-label">Agreed Commission (AED) *</Label>
                        <div className="form-control-wrap">
                            <Input
                                type="text"
                                name="agreedCommissionValue"
                                value={dealInfo.agreedCommissionValue}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9,]/g, '');
                                    setDealInfo({
                                        ...dealInfo,
                                        agreedCommissionValue: value
                                    });
                                    // Update percentage field
                                    const price = +dealInfo.estimatedPrice.toString().replaceAll(',', '');
                                    const commissionValue = +value.toString().replaceAll(',', '');
                                    if (!isNaN(price) && price > 0 && commissionValue) {
                                        const percent = (commissionValue / price) * 100;
                                        setDealInfo((prev) => ({
                                            ...prev,
                                            agreedCommission: percent,
                                        }));
                                        setFormattedCommission(percent.toFixed(2));
                                    }
                                }}
                            />
                            {errors.agreedCommissionValue && <span className="text-danger">{errors.agreedCommissionValue}</span>}
                        </div>
                    </div>
                </Col>

                <Col md="12">
                    <div className="form-group">
                        <Label className="form-label">Notes</Label>
                        <div className="form-control-wrap">
                            <Input
                                type="textarea"
                                name="notes"
                                value={dealInfo.notes}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                </Col>

                <Col md="12">
                    <Button color="primary" type="submit" disabled={loading} onClick={handleSubmit}>
                        {loading ? <Spinner size="sm" /> : 'Create Expression of Interest'}
                    </Button>
                </Col>
            </Row>

        </PreviewCard>
    );
};

export default EOIForm;

